import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { FaCalendarAlt } from "react-icons/fa";
import { MdOutlineClose } from "react-icons/md";
import {
  useCancelReservationMutation,
  useGetAllDatesQuery,
} from "../../../store/api/school/ReservationSlice";
import { toast } from "react-toastify";

const CancelReservation = ({
  showCancelModal,
  handleCloseCancelModal,
  handleCancelReservation,
  reservationId,
  charterFrequency,
  endPoint,
}) => {
  const { t } = useTranslation();
  const [allDates, setAllDates] = useState([]);
  const [error, setError] = useState({
    cancelType: "",
    dateList: "",
  });
  const [formData, setFormData] = useState({
    cancelType: "",
    notes: "",
    singleDate: null,
    dateList: [],
  });

  const [cancelReservation, { isLoading }] = useCancelReservationMutation();
  const {
    data,
    isLoading: loadingDates,
    refetch,
  } = useGetAllDatesQuery(reservationId, {
    skip: !reservationId,
  });

  // Reset on open/close
  useEffect(() => {
    if (!showCancelModal) {
      setFormData({
        cancelType: "",
        notes: "",
        singleDate: null,
        dateList: [],
      });
      setError({ cancelType: "", dateList: "" });
    }
  }, [showCancelModal]);

  useEffect(() => {
    if (data?.data?.instances) {
      const all = data.data.instances.map((item) => ({
        date: new Date(item.date),
        status: item.status,
      }));
      setAllDates(all);
    }
  }, [data]);

  const handleChangeRadio = (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      cancelType: value,
      ...(value === "one_time" ? { dateList: [] } : {}),
    }));

    // remove error
    setError((prev) => ({
      ...prev,
      cancelType: "",
    }));
  };

  const handleAddDate = () => {
    if (!formData.singleDate) return;

    const formattedDate = moment(formData.singleDate).format("YYYY-MM-DD");
    if (formData.dateList.includes(formattedDate)) {
      toast.warning(t("dateAlreadyAdded"));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      dateList: [...prev.dateList, formattedDate],
      singleDate: null,
    }));

    // remove date error
    setError((prev) => ({
      ...prev,
      dateList: "",
    }));
  };

  const handleRemoveDate = (date) => {
    setFormData((prev) => ({
      ...prev,
      dateList: prev.dateList.filter((d) => d !== date),
    }));
  };

  const handleSubmit = async () => {
    setError({ cancelType: "", dateList: "" });

    // Radio Validation
    if (!formData.cancelType) {
      setError((prev) => ({
        ...prev,
        cancelType: t("selectCancelType"),
      }));
      return;
    }

    // Date Validation only for specific date
    if (formData.cancelType === "recurring" && formData.dateList.length === 0) {
      setError((prev) => ({
        ...prev,
        dateList: t("addAtLeastOneDate"),
      }));
      return;
    }

    const payload = {
      cancel_type:
        formData.cancelType === "one_time" ? "complete" : "specific_date",
      cancellation_reason: formData.notes,
      ...(formData.cancelType === "recurring" && formData.dateList.length > 0
        ? { instance_date: formData.dateList }
        : {}),
    };

    try {
      const res = await cancelReservation({
        endPoint,
        reservationId,
        payload,
      }).unwrap();
      handleCloseCancelModal();
      toast.success(res?.message || "Reservation canceled successfully!");
      await refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to cancel reservation");
    }
  };

  const isCancelledDate = (date) => {
    return allDates.some(
      (d) =>
        d.status === "cancelled" &&
        moment(d.date).format("YYYY-MM-DD") ===
          moment(date).format("YYYY-MM-DD"),
    );
  };
  const isScheduledDate = (date) => {
    return allDates.some(
      (d) =>
        d.status === "scheduled" &&
        moment(d.date).format("YYYY-MM-DD") ===
          moment(date).format("YYYY-MM-DD"),
    );
  };

  return (
    <>
      <Modal
        show={showCancelModal}
        onHide={handleCloseCancelModal}
        size="md"
        centered
        backdrop="static"
        className="Model"
      >
        <Modal.Header closeButton className="border-0 position-relative">
          <Modal.Title className="w-100 text-center">
            <h4 className="mb-0 fw-bold text-white">
              <i className="fas fa-times-circle me-2"></i>
              {t("cancel")} {t("reservation")}
            </h4>
          </Modal.Title>
        </Modal.Header>
        <div id="datePickerPortal"></div>
        <Modal.Body
          className="pt-[35px]"
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* Warning Notice */}
          <div
            className="mb-3 p-2 text-center"
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              fontSize: "0.82rem",
              color: "#856404",
            }}
          >
            ⚠️ This action <strong>cannot be undone</strong>. The selected dates
            or the entire reservation will be permanently cancelled.
          </div>

          <div className="info-card mb-3">
            <div className="info-body">
              {/* Radio Buttons */}
              <div className="mb-2 p-2 d-flex justify-content-between align-items-center">
                {/* Complete Reservation – ALWAYS SHOW */}
                <label className="m-0">
                  <span className="radioWrapper">
                    <input
                      className="radioInpt"
                      type="radio"
                      value="one_time"
                      name="booking_type"
                      checked={formData.cancelType === "one_time"}
                      onChange={handleChangeRadio}
                    />
                    <i className="bg"></i>
                  </span>{" "}
                  {t("complete")} {t("reservation")}
                </label>

                {/* Specific Date – FOR RECURRING BUS CHARTER OR BUSING SUBSCRIPTION */}
                {(charterFrequency === "recurring" || endPoint === "busing-subscription") && (
                  <label className="m-0">
                    <span className="radioWrapper">
                      <input
                        className="radioInpt"
                        type="radio"
                        value="recurring"
                        name="booking_type"
                        checked={formData.cancelType === "recurring"}
                        onChange={handleChangeRadio}
                      />
                      <i className="bg"></i>
                    </span>{" "}
                    {t("specific")} {t("date")}
                  </label>
                )}
              </div>

              {error.cancelType && (
                <div className="text-danger mb-3">{error.cancelType}</div>
              )}

              {/* Specific Date Div */}
              {formData.cancelType === "recurring" && (
                <div className="mb-3 p-2 border rounded">
                  <div className="d-flex gap-2 align-items-center">
                    <div className="position-relative w-100">
                      <ReactDatePicker
                        className="form-control"
                        selected={formData.singleDate}
                        onChange={(date) =>
                          setFormData((prev) => ({ ...prev, singleDate: date }))
                        }
                        dateFormat="yyyy-MM-dd"
                        placeholderText={`${t("select")} ${t("date")}`}
                        /* 👇 IMPORTANT FIX */
                        preSelection={null}
                        disabledKeyboardNavigation
                        includeDates={allDates.map((d) => d.date)}
                        filterDate={(date) => {
                          const formatted = moment(date).format("YYYY-MM-DD");

                          // Already added dates disable
                          if (formData.dateList.includes(formatted))
                            return false;

                          const found = allDates.find(
                            (d) =>
                              moment(d.date).format("YYYY-MM-DD") === formatted,
                          );
                          return found?.status !== "cancelled";
                        }}
                        dayClassName={(date) => {
                          const found = allDates.find(
                            (d) =>
                              moment(d.date).format("YYYY-MM-DD") ===
                              moment(date).format("YYYY-MM-DD"),
                          );
                          return found?.status === "cancelled"
                            ? "cancelled-date"
                            : "";
                        }}
                        portalId="datePickerPortal"
                        popperPlacement="bottom-start"
                      />

                      <FaCalendarAlt className="calederIcon" />
                    </div>

                    <button className="btn btn-primary" onClick={handleAddDate}>
                      {t("add")}
                    </button>
                  </div>

                  {/* Date List */}
                  {formData.dateList.length > 0 && (
                    <div className="mt-2">
                      {formData.dateList.map((date, idx) => (
                        <div
                          key={idx}
                          className="d-flex justify-content-between align-items-center mt-1 p-2 border rounded"
                        >
                          <span>{date}</span>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveDate(date)}
                          >
                            <MdOutlineClose />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {formData.cancelType === "recurring" && (
                    <div>
                      {error.dateList && (
                        <div className="text-danger mt-2">{error.dateList}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Textarea */}
              <div className="mb-2">
                <label className="fw-semibold mb-1">
                  <i className="fas fa-comment-dots me-2"></i>
                  {t("reason")}
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder={`${t("enter")} ${t("cancellation")} ${t(
                    "reason",
                  )}...`}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={handleCloseCancelModal}
            >
              {t("close")}
            </Button>
            <Button
              className="smallBtn auto"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              ) : (
                <>
                  <i className="fas fa-times me-2"></i>
                  {t("cancel")}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <style jsx>{`
        .detailModel .modal-dialog {
          max-width: 900px;
        }

        /* ... your existing styles ... */

        /* Cancelled date style */
        .cancelled-date {
          background-color: #ff4d4d !important;
          color: white !important;
          border-radius: 50% !important;
        }

        /* Dates not in list */
        .not-in-list {
          color: #999 !important;
          opacity: 0.6;
        }

        /* Disabled day */
        .react-datepicker__day--disabled {
          pointer-events: none;
        }
        .react-datepicker__day--today {
          font-weight: normal !important;
          border: none !important;
        }
      `}</style>
    </>
  );
};

export default CancelReservation;
